import {createHostBridge, type HostBridge} from './bridge/host-bridge'
import {
  DEFAULT_FONT_FAMILY,
  getGoogleFontsHref,
  getShellFontStack as buildShellFontStack,
  resolveShellFontFamily,
} from './fonts'
import type {AddToCartResponse} from './bridge/messages'
import {ensureShopperAndSessionIds, safeSetToLocalStorage, upsertExperienceActivity} from './session'
import {classifyShopifyCartAddFailure} from './shopify-cart-add'
import type {AnagramMessage, EnvironmentConfig, ShellConfig} from './types'

const MoneySubunitDivisor = 100

type AddToCartResponseBody = Omit<AddToCartResponse, 'kind'>

/**
 * Event names dispatched on the host page after a successful cart mutation.
 *
 * There is no single cross-theme standard for cart change notifications, so we
 * fan out the most common ones. Themes that do not listen for any of these
 * events will simply not react; we intentionally do *not* splice section
 * markup into the host DOM because that is too invasive a side effect for
 * arbitrary merchant themes.
 */
const HostCartChangeEventNames = ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build'] as const

type ActivityMessage = Extract<AnagramMessage, {type: 'ANAGRAM_ACTIVITY'}>

function isActivityMessage(msg: AnagramMessage): msg is ActivityMessage {
  return (
    msg.type === 'ANAGRAM_ACTIVITY' &&
    typeof msg.key === 'string' &&
    typeof msg.value === 'object' &&
    msg.value !== null &&
    typeof msg.value.experienceId === 'string' &&
    typeof msg.value.lastUsedDate === 'string'
  )
}

function notifyHostCartThemesOfChange(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  const detail = {source: 'anagram', cartData: undefined}
  const targets: EventTarget[] = [document, document.documentElement, window]

  for (const target of targets) {
    for (const eventName of HostCartChangeEventNames) {
      try {
        target.dispatchEvent(new CustomEvent(eventName, {bubbles: true, detail}))
      } catch {
        // Ignore environments where CustomEvent is unavailable.
      }
    }
  }
}

export type SdkLiveResource = 'experiences' | 'placements'

/**
 * Construction options shared by every shell. `liveResource` is the
 * placement-vs-experience fork: it selects which `/live/{resource}/{id}` route
 * the iframe loads. Defined here (not per-shell) so the fork has a single
 * contract that every concrete shell forwards unchanged to `super`.
 */
export type ShellOptions = {
  fromPreview?: boolean
  liveResource?: SdkLiveResource
  /** True when parentUrl was intentionally supplied by the boot script. */
  hasExplicitParentUrl?: boolean
  parentUrl?: string
  previewConfig?: ShellConfig
  /**
   * Present the panel as a right-hand sheet that becomes a bottom drawer on
   * narrow screens. Only set by the opt-in `data-panel-layout="side-sheet"`
   * boot attribute (see `side-sheet-layout.ts`).
   */
  sideSheetLayout?: boolean
}

export abstract class Shell {
  protected readonly config: EnvironmentConfig
  protected readonly experienceId: string
  protected readonly liveResource: SdkLiveResource
  protected readonly isFromPreview: boolean
  protected readonly hasExplicitParentUrl: boolean
  protected readonly parentUrl: string | undefined
  protected activePreviewConfig: ShellConfig | undefined
  protected readonly usesSideSheetLayout: boolean
  private iframeMessageListener: ((event: MessageEvent) => void) | null = null
  private hostBridge: HostBridge | null = null

  constructor(config: EnvironmentConfig, experienceId: string, options?: ShellOptions) {
    this.config = config
    this.experienceId = experienceId
    this.liveResource = options?.liveResource ?? 'experiences'
    this.isFromPreview = options?.fromPreview === true
    this.hasExplicitParentUrl = options?.hasExplicitParentUrl === true
    this.parentUrl = options?.parentUrl
    this.activePreviewConfig = options?.previewConfig
    this.usesSideSheetLayout = options?.sideSheetLayout === true
  }

  protected resolveIframeParentUrl(): string | undefined {
    if (this.isFromPreview || this.hasExplicitParentUrl) {
      return this.parentUrl
    }

    return window.location.href
  }

  protected getLiveIframePageContext(): {parentUrl: string; pageTitle: string} {
    return {
      parentUrl: this.resolveIframeParentUrl() ?? window.location.href,
      pageTitle: document.title,
    }
  }

  abstract mount(): void

  /** Apply a same-form-factor preview config update without tearing down the shell. */
  applyPreviewShellConfig(_config: ShellConfig): boolean {
    return false
  }

  protected updateActivePreviewConfig(config: ShellConfig | undefined): void {
    this.activePreviewConfig = config
  }

  protected buildIframeContextKey(parentUrl: string, pageTitle: string): string {
    return JSON.stringify({
      parentUrl,
      pageTitle,
      previewConfig: this.activePreviewConfig ? JSON.stringify(this.activePreviewConfig) : null,
    })
  }

  destroy(): void {
    this.stopListeningForIframeMessages()
  }

  protected createIframeUrl(mode: 'inline' | 'sheet'): URL {
    const url = new URL(`/live/${this.liveResource}/${this.experienceId}`, this.config.baseUrl)
    url.searchParams.set('mode', mode)
    if (this.isFromPreview) {
      url.searchParams.set('fromPreview', 'true')
      if (this.activePreviewConfig) {
        url.searchParams.set('previewConfig', JSON.stringify(this.activePreviewConfig))
      }
    }

    const {shopperId, sessionId, stableId} = ensureShopperAndSessionIds()
    if (shopperId) url.searchParams.append('shopperId', shopperId)
    if (sessionId) url.searchParams.append('sessionId', sessionId)
    if (stableId) url.searchParams.append('stableId', stableId)

    return url
  }

  protected createIframe(url: URL): HTMLIFrameElement {
    const iframe = document.createElement('iframe')
    iframe.src = url.toString()
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = 'none'
    iframe.setAttribute('allow', 'geolocation')
    return iframe
  }

  protected getShellFontFamily(fontFamily: string): string {
    return resolveShellFontFamily(fontFamily)
  }

  protected getShellFontStack(fontFamily: string): string {
    return buildShellFontStack(fontFamily)
  }

  protected attachFontStylesheet(shadowRoot: ShadowRoot, fontFamily: string): void {
    const resolvedFontFamily = this.getShellFontFamily(fontFamily)
    const href = getGoogleFontsHref(
      resolvedFontFamily === DEFAULT_FONT_FAMILY ? [DEFAULT_FONT_FAMILY] : [resolvedFontFamily, DEFAULT_FONT_FAMILY]
    )

    // Load fonts at the document level so shadow DOM descendants can reliably
    // resolve the actual font face instead of falling back while still
    // reporting the configured family name in computed styles.
    const existingDocumentLink = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).find(
      (link) => (link as HTMLLinkElement).href === href
    )

    if (!existingDocumentLink) {
      const documentLink = document.createElement('link')
      documentLink.rel = 'stylesheet'
      documentLink.href = href
      document.head.appendChild(documentLink)

      // Force the browser to download the font by creating a hidden element in the light DOM.
      // This works around a known Chrome issue where fonts defined in the light DOM
      // are not downloaded if they are only used inside a ShadowRoot.
      const hiddenEl = document.createElement('div')
      hiddenEl.style.fontFamily = this.getShellFontStack(fontFamily)
      hiddenEl.style.position = 'absolute'
      hiddenEl.style.visibility = 'hidden'
      hiddenEl.style.width = '0'
      hiddenEl.style.height = '0'
      hiddenEl.style.pointerEvents = 'none'
      hiddenEl.textContent = 'A'
      document.body.appendChild(hiddenEl)
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    shadowRoot.appendChild(link)
  }

  protected styleAsViewportOverlay(host: HTMLElement): void {
    const s = host.style
    s.setProperty('display', 'block', 'important')
    s.setProperty('position', 'fixed', 'important')
    s.setProperty('top', '0', 'important')
    s.setProperty('right', '0', 'important')
    s.setProperty('bottom', '0', 'important')
    s.setProperty('left', '0', 'important')
    s.setProperty('z-index', '2147483647', 'important')
    s.setProperty('pointer-events', 'none', 'important')
    s.setProperty('overflow', 'visible', 'important')
    s.setProperty('margin', '0', 'important')
    s.setProperty('padding', '0', 'important')
    s.setProperty('border', 'none', 'important')
    s.setProperty('background', 'none', 'important')
    s.setProperty('visibility', 'visible', 'important')
    s.setProperty('opacity', '1', 'important')
  }

  protected styleAsInlineHost(host: HTMLElement): void {
    const s = host.style
    s.setProperty('display', 'block', 'important')
    s.setProperty('visibility', 'visible', 'important')
    s.setProperty('opacity', '1', 'important')
  }

  protected ownsIframeMessageSource(_source: MessageEventSource): boolean {
    return true
  }

  /**
   * Host-page listener for messages sent by the live-experience iframe.
   * Messages the host sends back into the iframe are posted by each shell.
   */
  protected startListeningForIframeMessages(): void {
    if (this.iframeMessageListener) return

    this.iframeMessageListener = (event: MessageEvent) => {
      if (!this.config.allowedOrigins.includes(event.origin)) return

      try {
        const msg = event.data as AnagramMessage | undefined
        if (!msg?.type) return
        if (event.source && !this.ownsIframeMessageSource(event.source)) return

        if (this.handleHostLevelIframeMessage(msg)) return

        this.handleShellSpecificIframeMessage(msg)
      } catch (error) {
        console.error('[Anagram] Error handling message:', error)
      }
    }

    window.addEventListener('message', this.iframeMessageListener)

    this.hostBridge = createHostBridge({
      window,
      allowedOrigins: this.config.allowedOrigins,
      responseTargetOrigin: new URL(this.config.baseUrl).origin,
      handlers: {
        ADD_TO_CART: (request, ctx) => {
          if (!this.ownsIframeMessageSource(ctx.source)) {
            return Promise.resolve({success: false, errorType: 'host_unavailable'})
          }
          return this.addVariantToHostCart(request.variantId, request.quantity)
        },
      },
    })
  }

  protected stopListeningForIframeMessages(): void {
    if (this.hostBridge) {
      this.hostBridge.destroy()
      this.hostBridge = null
    }
    if (!this.iframeMessageListener) return
    window.removeEventListener('message', this.iframeMessageListener)
    this.iframeMessageListener = null
  }

  private async addVariantToHostCart(variantId: string, quantity: number): Promise<AddToCartResponseBody> {
    const shopifyRoot = (window as Window & {Shopify?: {routes?: {root?: string}}}).Shopify?.routes?.root
    if (!shopifyRoot) {
      return {success: false, errorType: 'host_unavailable'}
    }

    try {
      const addResponse = await fetch(`${shopifyRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{id: variantId, quantity}],
        }),
      })

      if (!addResponse.ok) {
        let errorBody: unknown
        try {
          errorBody = await addResponse.json()
        } catch {
          errorBody = undefined
        }
        return {
          success: false,
          errorType: classifyShopifyCartAddFailure(errorBody),
        }
      }

      const addData = (await addResponse.json()) as {
        items?: Array<{
          variant_id?: number
          quantity?: number
          final_line_price?: number
          line_price?: number
          price?: number
        }>
      }

      notifyHostCartThemesOfChange()

      const matchedLineItem =
        addData.items?.find((item) => item.variant_id != null && String(item.variant_id) === variantId) ??
        addData.items?.at(0)

      let currencyCode: string | undefined
      try {
        const cartResponse = await fetch(`${shopifyRoot}cart.js`)
        if (cartResponse.ok) {
          const cartData = (await cartResponse.json()) as {currency?: string}
          currencyCode = cartData.currency
        }
      } catch {
        // Currency enrichment is best-effort; success should still be returned if the add succeeds.
      }

      const quantityForLine = matchedLineItem?.quantity ?? quantity
      const centsValue =
        matchedLineItem?.final_line_price ??
        matchedLineItem?.line_price ??
        (typeof matchedLineItem?.price === 'number' ? matchedLineItem.price * quantityForLine : undefined)

      return {
        success: true,
        currencyCode,
        cartLineCostAmount: typeof centsValue === 'number' ? centsValue / MoneySubunitDivisor : undefined,
      }
    } catch {
      return {success: false, errorType: 'rejected'}
    }
  }

  private handleHostLevelIframeMessage(msg: AnagramMessage): boolean {
    switch (msg.type) {
      case 'ANAGRAM_READY': {
        if ('experienceId' in msg && msg.experienceId === this.experienceId) {
          this.onIframeReady()
        }
        return true
      }
      case 'ANAGRAM_OPEN_URL': {
        if (!msg.url) return true
        const isTelOrMailto = /^(tel|mailto):/i.test(msg.url)
        if (isTelOrMailto) {
          window.location.href = msg.url
        } else {
          window.open(msg.url, '_blank', 'noopener')
        }
        return true
      }
      case 'ANAGRAM_ACTIVITY': {
        if (!isActivityMessage(msg)) return true
        safeSetToLocalStorage(msg.key, msg.value)
        try {
          upsertExperienceActivity(String(msg.value.experienceId), String(msg.value.lastUsedDate))
        } catch {
          // Activity tracking is best-effort and must not break the host shell.
        }
        ensureShopperAndSessionIds()
        return true
      }
      default:
        return false
    }
  }

  /** Called after the child iframe announces it has loaded. */
  protected abstract onIframeReady(): void
  /** Handles iframe-to-host messages that only a concrete shell understands. */
  protected abstract handleShellSpecificIframeMessage(msg: AnagramMessage): void
}
